import React, { useEffect, useState } from "react";
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  Image,
  StyleSheet,
  ScrollView,
  Dimensions,
  PixelRatio,
  Modal,
  ActivityIndicator,
  Platform,
} from "react-native";
import * as ImagePicker from "expo-image-picker"; // or your preferred library
import { useNavigation, useRoute } from "@react-navigation/native";
import SuccessAlertComp from "../../alertComp/SuccessAlertComp";
import ErrorAlertComp from "../../alertComp/ErrorAlertComp";
import axios from "../../api/axios";
import useAuth from "../../hooks/useAuth";
import Colours from "../../utils/Colours";

const { width, height } = Dimensions.get("window");

// --- RESPONSIVE TOOLKIT ---
const BASE_WIDTH = 375;
const BASE_HEIGHT = 812;

const dynamicFontSize = (size) => {
  const scaleFactor = Math.min(width / BASE_WIDTH, height / BASE_HEIGHT);
  return Math.round(PixelRatio.roundToNearestPixel(size * scaleFactor));
};

const dynamicSpacing = (size) => (width / BASE_WIDTH) * size;
const dynamicSize = (size) => (width / BASE_WIDTH) * size;

const PersonalDetailsScreen = () => {
  const { isGuest, image, username, phoneNumber } = useAuth();

  const navigation = useNavigation();
  const route = useRoute();
  const { userstats } = route.params || {};
  // --- STATE MANAGEMENT ---
  // const [profileImage, setProfileImage] = useState(
  //   userstats?.data?.profilePicture || profilePicture ?? "https://api.dicebear.com/7.x/pixel-art/svg?seed=user",
  // );
  const [profileImage, setProfileImage] = useState(
    userstats?.data?.profilePicture ||
      image ||
      "https://api.dicebear.com/7.x/pixel-art/svg?seed=user",
  );
  const [isErrorCompVisible, setIsErrorCompVisible] = useState(false);
  const [isSuccessCompVisible, setIsSuccessCompVisible] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoadingImage, setIsLoadingImage] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    //console.log("userstats", userstats);
  }, []);

  const pickImage = async () => {
    try {
      setIsLoadingImage(true);

      let result = await ImagePicker.launchImageLibraryAsync({
        mediaTypes: ImagePicker.MediaTypeOptions.Images,
        allowsEditing: true,
        aspect: [1, 1],
        quality: 1,
      });
      // if (!result.canceled) setProfileImage(result.assets[0].uri);

      if (result.canceled) return; // Exit early if the user canceled

      const currentAsset = result.assets[0];
      const imageUri = currentAsset.uri;
      let fileSizeInMB = 0;

      // 🌟 CROSS-PLATFORM FILE SIZE CHECK (Web safe)
      if (Platform.OS === "web") {
        if (currentAsset.file) {
          fileSizeInMB = currentAsset.file.size / (1024 * 1024);
        } else {
          // Fallback web blob fetch evaluation if the file handle isn't attached directly
          const response = await fetch(imageUri);
          const blob = await response.blob();
          fileSizeInMB = blob.size / (1024 * 1024);
        }
      } else {
        // Native Device file size check using expo-file-system
        const fileInfo = await FileSystem.getInfoAsync(imageUri);
        fileSizeInMB = fileInfo.size / (1024 * 1024);
      }

      // Guardrail size boundary validation check
      if (fileSizeInMB > 65) {
        setErrorMessage(
          `Image too large: The selected image is ${fileSizeInMB.toFixed(2)} MB. Please choose another.`,
        );
        setIsErrorCompVisible(true);
        return; // Stop further state execution
      }

      // Update your hook state with the verified safe image URI string
      setProfileImage(imageUri);
    } catch (error) {
      console.error("Error picking image:", error);
    } finally {
      setIsLoadingImage(false); // Clean up the loading wheel state regardless of outcome
    }
  };

  const handleContinue = async () => {
    // 1. Validation Logic
    if (!firstName.trim() || !lastName.trim() || !email.trim() || !password.trim()) {
      setErrorMessage("Required Fields", "Please fill in all forms to continue.");
      setIsErrorCompVisible(true);
      //   Alert.alert("Required Fields", "Please fill in all forms to continue.");
      return;
    }

    //NEW VALIDATION: Force image selection before continuing
    if (!profileImage) {
      setErrorMessage(
        "Profile Photo Required",
        "Please select a profile picture to complete your registration.",
      );
      setIsErrorCompVisible(true);
      return;
    }

    // Basic Email Regex
    const emailRegex = /\S+@\S+\.\S+/;
    if (!emailRegex.test(email)) {
      setErrorMessage("Invalid Email", "Please enter a valid email address.");
      setIsErrorCompVisible(true);
      return;
    }

    try {
      setLoading(true);
      const response = await axios.get(
        `/emailChecking/emailChecking/${email.trim().toLowerCase()}`,
      );

      // 4. HANDLE SUCCESS
      if (response.data.available) {
        //console.log("response.data.message", response.data.message);
        // Just navigate and pass the current state as a "bundle"
        if (isGuest) {
          //when accessToken still exists, i.e when the user is logged in
          navigation.navigate("user_identity", {
            registrationData: {
              username: userstats?.data?.username || username,
              phoneNumber: userstats?.data?.phoneNumber || phoneNumber,
              firstname: firstName,
              lastname: lastName,
              email: email.toLowerCase(),
              password,
              profilePicture: profileImage,
            },
          });
        } else {
          navigation.navigate("identityScreen", {
            registrationData: {
              username: userstats?.data?.username,
              phoneNumber: userstats?.data?.phoneNumber,
              firstname: firstName,
              lastname: lastName,
              email: email.toLowerCase(),
              password,
              profilePicture: profileImage,
            },
          });
        }
      } else {
        setErrorMessage(response.data.message || "This email have already been registered");
        setIsErrorCompVisible(true);
      }
    } catch (error) {
      console.error("Critical Error:", error);
      const errorMsg = error.response?.data?.message || "An unexpected error occurred.";
      setErrorMessage(errorMsg);
      setIsErrorCompVisible(true);
    } finally {
      setLoading(false); // Stop spinner globally for both success and catches
    }
  };

  return (
    <ScrollView style={styles.container} contentContainerStyle={styles.content}>
      <Text style={styles.headerTitle}>
        {userstats?.isNewUser || isGuest ? "Finish Setting Up" : "Complete your profile"}
      </Text>

      {/* Changeable Profile Pic */}
      <TouchableOpacity style={styles.imagePickerWrapper} onPress={pickImage}>
        {isLoadingImage ? (
          <ActivityIndicator size={"small"} color={Colours.PRIMARY} />
        ) : (
          <>
            <Image source={{ uri: profileImage }} style={styles.profilePic} />
            <View style={styles.editBadge}>
              <Text style={{ color: "#FFF", fontSize: 10 }}>Edit</Text>
            </View>
          </>
        )}
      </TouchableOpacity>

      <View style={styles.formSection}>
        {/* Row for First & Last Name */}
        <View style={styles.row}>
          <View style={[styles.inputGroup, { flex: 1, marginRight: 10 }]}>
            <Text style={styles.label}>First Name</Text>
            <TextInput
              style={styles.input}
              placeholder="John"
              value={firstName}
              onChangeText={setFirstName}
            />
          </View>
          <View style={[styles.inputGroup, { flex: 1 }]}>
            <Text style={styles.label}>Last Name</Text>
            <TextInput
              style={styles.input}
              placeholder="Doe"
              value={lastName}
              onChangeText={setLastName}
            />
          </View>
        </View>

        <View style={styles.inputGroup}>
          <Text style={styles.label}>Email Address</Text>
          <TextInput
            style={styles.input}
            placeholder="john@example.com"
            keyboardType="email-address"
            autoCapitalize="none"
            value={email}
            onChangeText={setEmail}
          />
        </View>

        <View style={styles.inputGroup}>
          <Text style={styles.label}>Password</Text>
          <TextInput
            style={styles.input}
            placeholder="••••••••"
            secureTextEntry
            value={password}
            onChangeText={setPassword}
          />
        </View>
      </View>

      <TouchableOpacity style={styles.primaryButton} onPress={handleContinue}>
        {loading ? (
          <ActivityIndicator color={"white"} />
        ) : (
          <Text style={styles.buttonText}>Continue</Text>
        )}
      </TouchableOpacity>
      <Modal visible={isErrorCompVisible} transparent={true} animationType="fade">
        <ErrorAlertComp message={errorMessage} onClose={() => setIsErrorCompVisible(false)} />
      </Modal>
      <Modal visible={isSuccessCompVisible} transparent={true} animationType="fade">
        <SuccessAlertComp
          message={successMessage}
          onClose={() => {
            setIsSuccessCompVisible(false);
          }}
        />
      </Modal>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: "#FFF" },
  content: { padding: dynamicSpacing(25), alignItems: "center" },
  headerTitle: {
    fontSize: dynamicFontSize(22),
    fontFamily: "Sen-Bold",
    marginBottom: dynamicSpacing(30),
    alignSelf: "flex-start",
  },
  imagePickerWrapper: {
    width: dynamicSize(100),
    height: dynamicSize(100),
    marginBottom: dynamicSpacing(40),
  },
  profilePic: {
    width: "100%",
    height: "100%",
    borderRadius: dynamicSize(50),
    backgroundColor: "#F5F5F5",
  },
  editBadge: {
    position: "absolute",
    bottom: 0,
    right: 0,
    backgroundColor: "#000",
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 10,
    borderWidth: 2,
    borderColor: "#FFF",
  },
  formSection: { width: "100%" },
  row: { flexDirection: "row", marginBottom: dynamicSpacing(15) },
  inputGroup: { marginBottom: dynamicSpacing(20) },
  label: {
    fontSize: dynamicFontSize(14),
    fontFamily: "Sen-Bold",
    color: "#666",
    marginBottom: 8,
  },
  input: {
    height: dynamicSize(50),
    backgroundColor: "#F9F9F9",
    borderRadius: dynamicSize(12),
    paddingHorizontal: 15,
    borderWidth: 1,
    borderColor: "#EEE",
    fontSize: dynamicFontSize(16),
  },
  // Centralized Screen Styles
  centralizedContent: {
    alignItems: "center",
    paddingHorizontal: dynamicSpacing(40),
    width: "100%",
  },
  largeProfilePic: {
    width: dynamicSize(120),
    height: dynamicSize(120),
    borderRadius: dynamicSize(30),
    marginBottom: dynamicSpacing(20),
  },
  usernameWrapper: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#F5F5F5",
    borderRadius: dynamicSize(12),
    paddingHorizontal: 15,
    height: dynamicSize(55),
  },
  atSymbol: { fontSize: 18, fontWeight: "bold", color: "#AAA", marginRight: 5 },
  usernameInput: { flex: 1, fontSize: 18, fontWeight: "600" },
  primaryButton: {
    backgroundColor: "#000",
    height: dynamicSize(55),
    borderRadius: dynamicSize(12),
    justifyContent: "center",
    alignItems: "center",
    width: "100%",
  },
  buttonText: { color: "#FFF", fontSize: 16, fontWeight: "bold" },
});

export default PersonalDetailsScreen;
